// src/component/MM2024Dialog.jsx
import React from "react";
import {
  Dialog,
  DialogContent,
  Slide,
  Box,
  Stack,
  Typography,
  IconButton,
  useMediaQuery,
  Link,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

// ---- Transition ----
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * 2024年 マスターマインドメンバーズ 活動のダイジェストダイアログ
 */
export default function MM2024Dialog({ open, onClose, accent = "#5A35FF" }) {
  const muiTheme = useTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      TransitionComponent={Transition}
      scroll="paper"
      PaperProps={{
        sx: {
          maxWidth: "960px",
          width: "100%",
          mx: "auto",
          borderRadius: fullScreen ? 0 : 4,
          overflow: "hidden",
          boxShadow:
            "0 22px 60px rgba(15,10,40,0.35), 0 0 0 1px rgba(255,255,255,0.8)",
          background:
            "radial-gradient(circle at 0% 0%, #ffffff, #fdf7ff 45%, #f7fbff 100%)",
        },
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: `${accent}22`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: accent }} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 13,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              opacity: 0.65,
            }}
          >
            MASTERMIND MEMBERS 2024
          </Typography>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 800,
              letterSpacing: "0.04em",
            }}
          >
            2024 年 マスターマインドメンバーズ 始動
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      {/* スクロール領域 */}
      <DialogContent
        dividers
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 },
          maxHeight: fullScreen ? "calc(100vh - 64px)" : "70vh",
          overflowY: "auto",
        }}
      >

        {/* YouTube 埋め込み */}
        <Box sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 13, mb: 0.5 }}>
            2024 マスターマインドメンバーズ 始動ムービー（YouTube）
          </Typography>
          <Link
            href="https://youtu.be/jlwUYZj9Xsk?si=TLfxf7fS-F-DIbQq"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: 13 }}
          >
            https://youtu.be/jlwUYZj9Xsk?si=TLfxf7fS-F-DIbQq
          </Link>
        </Box>
        <YoutubeEmbed videoId="jlwUYZj9Xsk" />


        {/* =========================
            イントロ
           ========================= */}
        <SectionTitle
          accent={accent}
          label="2024 マスターマインドメンバーズ始動"
          subtitle="夢は「自分」で叶えるな、君は「仲間」の夢を叶えろ。"
        />


        <Typography
          sx={{
            fontSize: 13,
            lineHeight: 1.9,
            color: "rgba(40,40,60,0.85)",
            mb: 2.5,
          }}
        >
          夢は「自分」で叶えるな、君は「仲間」の夢を叶えろ。
          <br />
          <br />
          世界一の大富豪アンドリュー・カーネギーが
          <b>「最後の秘密」</b> として伝えた
          <b>“マスターマインド”</b>。
          <br />
          そして今この瞬間も、世界中の人間の「脳内」を揺らしている、
          地球固有の共振現象 <b>“シューマン共振”</b> を使った純正律の音源。
          <br />
          <br />
          同じ音源をメンバー全員で聴くことによって、
          <b>「同じ意識」＝「集合意識」</b> が形成されていきます。
          <br />
          毎日の「音トレ」で周波数を整え、
          仲間の夢も自分の夢もどんどん叶えていく――
          <br />
          それが、<b>マスターマインドメンバーズ 2024</b> の始まりでした。
        </Typography>


        {/* =========================
            活動の奇跡
           ========================= */}
        <SubSectionTitle accent={accent}>【 活動の奇跡 】</SubSectionTitle>

        {/* 前半：導入〜夏至・コース選択 */}
        <TimelineList
          items={[
            "3/20　＜春分点 12:06＞ 7.83Hz 「Leiの祈り」配信開始",
            "各自 DDP 登録",
            "5月　忘却期間",
            "6/21　＜夏至点 5:51＞ 7.86Hz 純正律音源 配信開始",
            "7月　コース選択",
          ]}
        />

        {/* Ⅰ. 絆を深める期間 */}
        <SubSectionTitle accent={accent}>
          ～ Ⅰ. 絆を深める期間 ～
        </SubSectionTitle>
        <TimelineList
          items={[
            "8/8　グループ発表",
            "9/21　＜秋分点 21:44＞ 7.89Hz 純正律音源 配信開始",
            "9月末　グループ相談会",
          ]}
        />

        {/* Ⅱ. DDPを深堀・理解する期間 */}
        <SubSectionTitle accent={accent}>
          ～ Ⅱ. DDPを深堀し、理解する期間 ～
        </SubSectionTitle>
        <TimelineList
          items={[
            "10月　DDP セッション台本 完成",
            "　　　DDP 添削機能を搭載した ChatGPT 期間限定公開",
            "10/20　第1回 全体 Zoom 会",
            "10/20　各都道府県チャット OPEN",
          ]}
        />

        {/* Ⅲ. DDPを叶えあう期間 */}
        <SubSectionTitle accent={accent}>
          ～ Ⅲ. DDPを叶えあう期間 ～
        </SubSectionTitle>
        <TimelineList
          items={[
            "11月　「〇〇さんのDDPが叶う30の理由」作成",
            "　　　ビジョンボード作成",
            "　　　マスターマインドメンバーズ限定 Tシャツ販売",
            "11/24　地域チャット公開",
            "11/17　第2回 全体 Zoom 会",
            "12/7–12/21　冬至まで聴く マスターマインド限定 特別音源公開",
            "12/22　第3回 全体 Zoom 会（予祝）",
            "12/21　＜冬至点 18:21＞ 7.86Hz 純正律音源 配信開始",
          ]}
        />

          {/* 画像 */}
          <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2.5}
          alignItems="stretch"
          sx={{ mb: 3 }}
        >
          {/* 左：画像（スクショ差し替え） */}
          <Box
            sx={{
              flex: 1,
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 14px 34px rgba(15,10,40,0.26)",
            }}
          >
            {/* ★ 実画像に差し替えてください */}
            <Box
              component="img"
              src="/images/mm2024_yosai_main.jpg"
              alt="マスターマインドメンバーズ2024 予祝シーン"
              sx={{ width: "100%", display: "block" }}
            />
          </Box>

          {/* 右：みつろうさんショット（任意） */}
          <Box
            sx={{
              flex: 1,
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 14px 34px rgba(15,10,40,0.22)",
            }}
          >
            {/* ★ 実画像に差し替え */}
            <Box
              component="img"
              src="/images/mm2024_mitsuro_talk.jpg"
              alt="さとうみつろう さんよりメッセージ"
              sx={{ width: "100%", display: "block", height: "100%" }}
            />
          </Box>
        </Stack>


        {/* クロージング */}
        <Box sx={{ mt: 4 }}>
          <Typography
            sx={{
              fontSize: 13,
              lineHeight: 1.9,
              color: "rgba(40,40,60,0.85)",
            }}
          >
            みんなの熱量をこのままで終えるワケにはいかない＾＾/
            <br />
            こうして 2024 年の歩みが、次の{" "}
            <b>マスターマインドメンバーズ 2025</b> へとつながっていきました。
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

/* ====== サブコンポーネント ====== */

function SectionTitle({ accent, label, subtitle }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.6 }}>
        <Box
          sx={{
            width: 10,
            height: 28,
            borderRadius: 999,
            background: `linear-gradient(180deg, ${accent}, ${accent}66)`,
          }}
        />
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 800,
            letterSpacing: "0.06em",
          }}
        >
          {label}
        </Typography>
      </Stack>
      {subtitle && (
        <Typography
          sx={{
            fontSize: 12,
            color: "rgba(40,40,60,0.7)",
            ml: 1.5,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function SubSectionTitle({ accent, children }) {
  return (
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 800,
        color: accent,
        mt: 2,
        mb: 1,
      }}
    >
      {children}
    </Typography>
  );
}

function TimelineList({ items }) {
  return (
    <Box
      component="ul"
      sx={{
        listStyle: "none",
        pl: 0,
        mb: 2.5,
        fontSize: 13,
        color: "rgba(40,40,60,0.85)",
      }}
    >
      {items.map((line) => (
        <Box
          component="li"
          key={line}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            mb: 0.4,
          }}
        >
          <Box
            sx={{
              mt: "7px",
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: "rgba(0,0,0,0.2)",
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: 13, lineHeight: 1.7 }}>
            {line}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/** YouTube 埋め込み（16:9・レスポンシブ） */
function YoutubeEmbed({ videoId }) {
  if (!videoId) return null;
  return (
    <Box
      sx={{
        mt: 1,
        mb: 3,
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
        position: "relative",
        pt: "56.25%", // 16:9
      }}
    >
      <Box
        component="iframe"
        src={`https://www.youtube.com/embed/${videoId}`}
        title="YouTube video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
        }}
      />
    </Box>
  );
}
